import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { OverlayContext, OverlayScopeContext } from './context';
import type { OverlayApi, OverlayScope, PopoverOptions } from './context';
import { Popover } from './Popover';
import type { PopoverEntry, PopoverOutcome } from './Popover';
import { useFocusGuard } from './focusGuard';
import type { Placement } from './placement';

let host: OverlayApi | null = null;

function requireHost(): OverlayApi {
  if (!host) throw new Error('浮层系统尚未挂载：请确认应用根节点已包裹 <OverlayProvider>');
  return host;
}

/**
 * 命令式浮层 API。用于事件回调里直接 `await overlay.confirm(...)`，
 * 无需在组件里取 hook。
 */
export const overlay: OverlayApi = {
  confirm: (options) => requireHost().confirm(options),
  alert: (options) => requireHost().alert(options),
  prompt: (options) => requireHost().prompt(options),
  dismissAll: () => requireHost().dismissAll(),
};

export interface OverlayProviderProps {
  children: ReactNode;
  scope?: OverlayScope;
}

/**
 * 统一浮层系统的根。
 *
 * 全站所有弹窗都必须经过这里：
 * - 按钮确认类 → 带 FLIP 动画的锚定弹窗（无阴影、位置按按钮固定缓存）
 * - 功能性弹窗 → 居中模态框（模糊背景），实时页自动降级为停靠面板
 * 同时这里也是唯一设置 `window.alert/confirm/prompt` 兜底拦截的入口。
 */
export function OverlayProvider({ children, scope = 'default' }: OverlayProviderProps) {
  const [current, setCurrent] = useState<PopoverEntry | null>(null);
  const currentRef = useRef<PopoverEntry | null>(null);
  const seq = useRef(0);
  // 位置缓存的生命周期 = 应用生命周期，保证同一个按钮的弹窗位置始终一致
  const sideCache = useRef(new Map<string, { bucket: string; side: Placement['side'] }>());

  // 焦点守卫：拦住非预期的主动聚焦，避免焦点被重挂载的按钮抢走（详见 focusGuard.ts）
  useFocusGuard(scope);

  const open = useCallback((options: PopoverOptions): Promise<PopoverOutcome> => {
    return new Promise<PopoverOutcome>((resolve) => {
      // 同一时刻只允许一个锚定浮层；新开一个视为对上一个的取消
      currentRef.current?.resolve({ kind: 'cancel' });
      seq.current += 1;
      const entry: PopoverEntry = { id: `ov_${seq.current}`, options, resolve };
      currentRef.current = entry;
      setCurrent(entry);
    });
  }, []);

  const handleSettled = useCallback((id: string, outcome: PopoverOutcome) => {
    const entry = currentRef.current;
    if (!entry || entry.id !== id) return;
    currentRef.current = null;
    setCurrent(null);
    entry.resolve(outcome);
  }, []);

  const api = useMemo<OverlayApi>(
    () => ({
      confirm: async (options) => (await open({ ...options, kind: 'confirm' })).kind === 'confirm',
      alert: async (options) => {
        await open({ ...options, kind: 'alert' });
      },
      prompt: async (options) => {
        const outcome = await open({ ...options, kind: 'prompt' });
        return outcome.kind === 'submit' ? outcome.value : null;
      },
      dismissAll: () => {
        const entry = currentRef.current;
        currentRef.current = null;
        setCurrent(null);
        entry?.resolve({ kind: 'cancel' });
      },
    }),
    [open],
  );

  useEffect(() => {
    host = api;
    return () => {
      if (host === api) host = null;
    };
  }, [api]);

  return (
    <OverlayScopeContext.Provider value={scope}>
      <OverlayContext.Provider value={api}>
        {children}
        {current ? <Popover entry={current} sideCache={sideCache.current} onSettled={handleSettled} /> : null}
      </OverlayContext.Provider>
    </OverlayScopeContext.Provider>
  );
}

/**
 * 只覆盖作用域、不重新创建浮层宿主的边界组件。
 * 单机管理页用它把自己标记为 `realtime`，从而禁用蒙版。
 */
export function OverlayScopeBoundary({ scope, children }: { scope: OverlayScope; children: ReactNode }) {
  return <OverlayScopeContext.Provider value={scope}>{children}</OverlayScopeContext.Provider>;
}

/** 供原生弹窗兜底拦截器复用：把原生调用转发到统一系统上。 */
export function forwardNativeAlert(message: string): void {
  if (!host) return;
  void host.alert({ placementKey: 'native-alert', trigger: document.body, title: '提示', description: message });
}
